// M10/10d — Side-by-side before/after screenshots for a proposed experiment.
// Non-technical owners can SEE the change before clicking Approve; technical
// owners still get the dom_changes list below.
//
// States, in priority order:
//   - Both URLs present            → render the pair + retake link
//   - Either URL missing, <90s old → auto-poll parent every 3s ("Generating preview…")
//   - Either URL missing, ≥90s old → "Preview unavailable" + prominent Retake button
//
// Card view crops the full-page PNG to a region around the bbox so the change
// dominates the frame. Modal opens in the same zoom with a toggle back to the
// full page. Fallback when bbox/viewport missing: full-page object-cover, same
// as the pre-crop behavior.

import React, { useEffect, useRef, useState } from "react"
import useApi from "../../hooks/useApi.js"
import ScreenshotModal from "./ScreenshotModal.jsx"

const POLL_INTERVAL_MS = 3000
const TIMEOUT_MS = 90_000
const TARGET_ASPECT = 4 / 3

function msSince(iso) {
  if (!iso) return Infinity
  const t = Date.parse(iso)
  if (Number.isNaN(t)) return Infinity
  return Date.now() - t
}

// Compute a crop rectangle (in full-page pixel space) centered on the bbox
// with padding for context. Returns { cx, cy, cw, ch } or null.
// See plan file for algorithm — pad by 2× bbox dimension OR viewport ratio,
// enforce 4:3, clamp to viewport bounds.
export function computeCropFrame({ bbox, viewport }) {
  if (!bbox || !viewport) return null
  const vw = Number(viewport.width)
  const vh = Number(viewport.height)
  const bx = Number(bbox.x)
  const by = Number(bbox.y)
  const bw = Number(bbox.width)
  const bh = Number(bbox.height)
  if (!vw || !vh || !bw || !bh) return null

  // Padding scales softly with bbox size. Too generous and a wide bbox (landscape
  // CTA button) inflates the crop to full-page width → no meaningful zoom. The
  // viewport-fraction floor keeps tiny bboxes from vanishing in a microscopic crop.
  const paddingX = Math.max(bw * 0.3, vw * 0.08)
  const paddingY = Math.max(bh * 0.3, vh * 0.03)

  let cw = bw + 2 * paddingX
  let ch = bh + 2 * paddingY

  cw = Math.max(cw, vw * 0.25)
  ch = Math.max(ch, vh * 0.10)

  // Enforce 4:3 by widening the short side.
  if (cw / ch < TARGET_ASPECT) cw = ch * TARGET_ASPECT
  else ch = cw / TARGET_ASPECT

  // Clamp to viewport. If the viewport is narrower than 4:3 (portrait page),
  // the aspect may need adjusting — just let it: the container's aspect stays
  // `cw/ch` and that's what matters.
  if (cw > vw) {
    cw = vw
    ch = Math.min(cw / TARGET_ASPECT, vh)
  }
  if (ch > vh) {
    ch = vh
    cw = Math.min(ch * TARGET_ASPECT, vw)
  }

  // Center on the bbox midpoint, then clamp so the crop stays inside the page.
  let cx = bx + bw / 2 - cw / 2
  let cy = by + bh / 2 - ch / 2
  cx = Math.max(0, Math.min(cx, vw - cw))
  cy = Math.max(0, Math.min(cy, vh - ch))

  return { cx, cy, cw, ch }
}

export default function ScreenshotPreview({ t, experiment, onRefresh }) {
  const api = useApi()
  const [retaking, setRetaking] = useState(false)
  const [modal, setModal] = useState(null) // "before" | "after" | null
  const [errorMsg, setErrorMsg] = useState(null)

  const { id, before_screenshot_url, after_screenshot_url, screenshot_bounding_box, screenshot_viewport, created_at } = experiment
  const bothReady = Boolean(before_screenshot_url && after_screenshot_url)
  const ageMs = msSince(created_at)
  const timedOut = !bothReady && ageMs > TIMEOUT_MS

  const crop = computeCropFrame({ bbox: screenshot_bounding_box, viewport: screenshot_viewport })

  // Auto-poll while the pair isn't ready + we haven't timed out. Lets the
  // owner sit on the Proposals page and watch previews land without ever
  // clicking refresh (matches STYLE_GUIDE §7 — site-wide live-progress rule).
  const timerRef = useRef(null)
  useEffect(() => {
    if (bothReady || timedOut) {
      if (timerRef.current) {
        clearInterval(timerRef.current)
        timerRef.current = null
      }
      return undefined
    }
    if (typeof onRefresh !== "function") return undefined

    timerRef.current = setInterval(() => {
      try { onRefresh() } catch { /* never break the dashboard */ }
    }, POLL_INTERVAL_MS)

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current)
        timerRef.current = null
      }
    }
  }, [bothReady, timedOut, onRefresh])

  async function handleRetake() {
    setErrorMsg(null)
    setRetaking(true)
    try {
      await api.post(`/api/v1/shop/experiments/${id}/retake_screenshots`)
      if (typeof onRefresh === "function") onRefresh()
    } catch (e) {
      setErrorMsg(e?.message || t("dashboard.screenshots.retake_error"))
    } finally {
      setRetaking(false)
    }
  }

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between">
        <p
          className="font-mono text-[10px] text-ink-tertiary"
          style={{ letterSpacing: "0.10em" }}
        >
          {t("dashboard.screenshots.section_label")}
        </p>
        {bothReady && (
          <button
            type="button"
            onClick={handleRetake}
            disabled={retaking}
            className="font-mono text-[10px] text-ink-faint hover:text-ai disabled:opacity-60"
            style={{ letterSpacing: "0.10em" }}
          >
            {retaking ? t("dashboard.screenshots.retaking") : t("dashboard.screenshots.retake")}
          </button>
        )}
      </div>

      {bothReady ? (
        <>
          <div className="grid gap-3 grid-cols-1 md:grid-cols-2">
            <ShotFrame
              t={t}
              label={t("dashboard.screenshots.before_caption")}
              captionColor="ink-tertiary"
              url={before_screenshot_url}
              bbox={screenshot_bounding_box}
              viewport={screenshot_viewport}
              crop={crop}
              onOpen={() => setModal("before")}
            />
            <ShotFrame
              t={t}
              label={t("dashboard.screenshots.after_caption")}
              captionColor="ai"
              withDot
              url={after_screenshot_url}
              bbox={screenshot_bounding_box}
              viewport={screenshot_viewport}
              crop={crop}
              onOpen={() => setModal("after")}
            />
          </div>
          {errorMsg && (
            <p className="text-[12px] text-failure">{errorMsg}</p>
          )}
        </>
      ) : timedOut ? (
        <FailureState t={t} onRetake={handleRetake} retaking={retaking} error={errorMsg} />
      ) : (
        <LoadingSkeleton t={t} />
      )}

      {modal && bothReady && (
        <ScreenshotModal
          t={t}
          which={modal}
          url={modal === "before" ? before_screenshot_url : after_screenshot_url}
          label={
            modal === "before"
              ? t("dashboard.screenshots.before_caption")
              : t("dashboard.screenshots.after_caption")
          }
          bbox={screenshot_bounding_box}
          viewport={screenshot_viewport}
          crop={crop}
          onClose={() => setModal(null)}
        />
      )}
    </section>
  )
}

function ShotFrame({ label, captionColor, withDot, url, bbox, viewport, crop, onOpen, t }) {
  // Cards are 4:3 when we have a crop (matches the crop rect); without a crop,
  // fall back to the old capped-aspect behavior so a selector-less proposal
  // still renders something reasonable.
  const aspect = crop ? "4 / 3" : fallbackAspect(viewport)

  return (
    <figure className="space-y-2">
      <figcaption className={`flex items-center gap-2 font-mono text-[10px] text-${captionColor}`} style={{ letterSpacing: "0.15em" }}>
        {withDot && (
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-ai" aria-hidden />
        )}
        {label}
      </figcaption>
      <button
        type="button"
        onClick={onOpen}
        aria-label={t("dashboard.screenshots.open_zoom", { label })}
        className="relative block w-full bg-surface-subtle border border-rule rounded-card overflow-hidden group"
        style={{ cursor: "zoom-in" }}
      >
        <div className="relative w-full" style={{ aspectRatio: aspect }}>
          <CroppedFrame url={url} crop={crop} viewport={viewport} bbox={bbox} pulseOnHover />
        </div>
      </button>
    </figure>
  )
}

// Renders the full-page PNG reframed to the `crop` rectangle. Uses percentage
// width/height so the img scales with the container on any display size.
// When crop is null, renders the full page with object-cover object-top —
// same behavior as pre-crop so selector-less proposals still show something.
export function CroppedFrame({ url, crop, viewport, bbox, pulseOnHover }) {
  if (!crop) {
    return (
      <>
        <img
          src={url}
          alt=""
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover object-top transition-opacity group-hover:opacity-95"
          draggable={false}
        />
        <BboxOverlay bbox={bbox} viewport={viewport} pulseOnHover={pulseOnHover} />
      </>
    )
  }

  const vw = Number(viewport.width)
  const vh = Number(viewport.height)
  const { cx, cy, cw, ch } = crop

  // Percentages relative to the container. Since the container aspect matches
  // cw/ch by construction, horizontal + vertical scale factors are equal, so
  // the image stays proportional.
  const imgWidthPct = (vw / cw) * 100
  const imgHeightPct = (vh / ch) * 100
  const leftPct = -(cx / cw) * 100
  const topPct = -(cy / ch) * 100

  return (
    <>
      <img
        src={url}
        alt=""
        loading="lazy"
        className="absolute transition-opacity group-hover:opacity-95"
        style={{
          width: `${imgWidthPct}%`,
          height: `${imgHeightPct}%`,
          left: `${leftPct}%`,
          top: `${topPct}%`,
          maxWidth: "none",
        }}
        draggable={false}
      />
      <CroppedBboxOverlay bbox={bbox} crop={crop} pulseOnHover={pulseOnHover} />
    </>
  )
}

function CroppedBboxOverlay({ bbox, crop, pulseOnHover }) {
  if (!bbox || !crop) return null
  const bw = Number(bbox.width) || 0
  const bh = Number(bbox.height) || 0
  if (bw <= 0 || bh <= 0) return null
  const { cx, cy, cw, ch } = crop
  const stroke = Math.max(ch * 0.006, 2)

  return (
    <svg
      className="absolute inset-0 w-full h-full pointer-events-none"
      viewBox={`0 0 ${cw} ${ch}`}
      preserveAspectRatio="none"
      aria-hidden
    >
      <rect
        x={(Number(bbox.x) || 0) - cx}
        y={(Number(bbox.y) || 0) - cy}
        width={bw}
        height={bh}
        fill="none"
        stroke="#0F766E"
        strokeWidth={stroke}
        className={pulseOnHover ? "group-hover:animate-pulse" : ""}
      />
    </svg>
  )
}

function BboxOverlay({ bbox, viewport, pulseOnHover }) {
  if (!bbox || !viewport) return null
  const vw = Number(viewport.width) || 1280
  const vh = Number(viewport.height) || 800
  const x = Number(bbox.x) || 0
  const y = Number(bbox.y) || 0
  const w = Number(bbox.width) || 0
  const h = Number(bbox.height) || 0
  if (w <= 0 || h <= 0) return null

  const stroke = Math.max(vh * 0.006, 4)

  return (
    <svg
      className="absolute inset-0 w-full h-full pointer-events-none"
      viewBox={`0 0 ${vw} ${vh}`}
      preserveAspectRatio="none"
      aria-hidden
    >
      <rect
        x={x}
        y={y}
        width={w}
        height={h}
        fill="none"
        stroke="#0F766E"
        strokeWidth={stroke}
        className={pulseOnHover ? "group-hover:animate-pulse" : ""}
      />
    </svg>
  )
}

function LoadingSkeleton({ t }) {
  return (
    <div className="bg-surface-card border border-rule rounded-card p-6">
      <div className="flex items-center gap-3 mb-4">
        <span className="inline-block w-2 h-2 rounded-full bg-ai animate-pulse" aria-hidden />
        <p className="font-mono text-[10px] text-ai" style={{ letterSpacing: "0.15em" }}>
          {t("dashboard.screenshots.generating_eyebrow")}
        </p>
      </div>
      <p className="text-[13px] text-ink-secondary" style={{ lineHeight: 1.55 }}>
        {t("dashboard.screenshots.generating_lede")}
      </p>
      <div className="mt-5 grid gap-3 grid-cols-1 md:grid-cols-2">
        <SkeletonShot />
        <SkeletonShot />
      </div>
    </div>
  )
}

function SkeletonShot() {
  return (
    <div
      className="bg-surface-subtle border border-rule rounded-card"
      style={{ aspectRatio: "4 / 3" }}
      aria-hidden
    />
  )
}

function FailureState({ t, onRetake, retaking, error }) {
  return (
    <div className="bg-surface-card border border-rule rounded-card p-6 space-y-3">
      <p className="font-mono text-[10px] text-ink-tertiary" style={{ letterSpacing: "0.15em" }}>
        {t("dashboard.screenshots.unavailable_eyebrow")}
      </p>
      <p className="text-[13px] text-ink-secondary" style={{ lineHeight: 1.55 }}>
        {t("dashboard.screenshots.unavailable_lede")}
      </p>
      <div className="flex items-center gap-3 pt-1">
        <button
          type="button"
          onClick={onRetake}
          disabled={retaking}
          className="bg-brand text-ink-on-dark rounded-button px-4 py-2 text-[13px] font-medium disabled:opacity-60"
        >
          {retaking ? t("dashboard.screenshots.retaking") : t("dashboard.screenshots.retake_primary")}
        </button>
        {error && <p className="text-[12px] text-failure">{error}</p>}
      </div>
    </div>
  )
}

// Full-scroll captures can be 3× viewport height (1280×2400). Raw ratio
// makes the card extremely tall; cap at 4:3 so the card stays scannable.
// Only reached when we don't have a crop — today that means no bbox was
// captured (AI selector resolved to nothing).
function fallbackAspect(viewport) {
  if (!viewport) return "4 / 3"
  const w = Number(viewport.width) || 1280
  const h = Number(viewport.height) || 800
  const raw = w / h
  return raw < 4 / 3 ? "4 / 3" : `${w} / ${h}`
}
